/**
 * MySQL sometimes returns JSON columns as strings in API payloads.
 * Always normalize before .map / nested access.
 */
export function parseJsonField<T = unknown>(raw: unknown, fallback: T): T {
  if (raw == null) return fallback

  let value: unknown = raw
  for (let i = 0; i < 2; i += 1) {
    if (typeof value !== 'string') break
    const trimmed = value.trim()
    if (!trimmed) return fallback
    try {
      value = JSON.parse(trimmed)
    } catch {
      return fallback
    }
  }

  return (value as T) ?? fallback
}

export function normalizeOrderCustomFields(raw: unknown): Array<{
  fieldId: number
  name: string
  type: string
  value: unknown
  priceDelta: number
  hideFromCustomer?: boolean
}> {
  const parsed = parseJsonField<unknown>(raw, [])
  if (!Array.isArray(parsed)) return []
  return parsed.map((field, index) => {
    const row = (field && typeof field === 'object' ? field : {}) as Record<string, unknown>
    return {
      fieldId: Number(row.fieldId) || index,
      name: String(row.name || ''),
      type: String(row.type || 'text'),
      value: row.value,
      priceDelta: Number(row.priceDelta) || 0,
      hideFromCustomer: row.hideFromCustomer === true,
    }
  })
}

/** Fields marked hideFromCustomer on the complex field definition / snapshot. */
export function isHiddenFromCustomerField(field: {
  hideFromCustomer?: boolean
}): boolean {
  return field.hideFromCustomer === true
}

export function withoutHiddenFromCustomerFields<T extends {
  hideFromCustomer?: boolean
}>(fields: T[] | null | undefined): T[] {
  if (!fields?.length) return []
  return fields.filter((field) => !isHiddenFromCustomerField(field))
}
